import React, { Fragment, Component } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, Modal, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import moment from 'moment';
import { gql } from 'apollo-boost';
import { Mutation, Query } from 'react-apollo';
import { Formik } from 'formik';
import { find, remove } from 'lodash';
import { Input } from 'reactstrap';
import Loader from '~/components/loader/loader';
import SubmissionButton from '~/components/submissionButton/submissionButton';
import { openResponse } from '~/utils/links';
import request from '~/utils/request';
import config from '../../config/config.json';

export const startLotsMutation = gql`
  mutation startLots($breweryId: Int!, $lots: [StartLot]!) {
    startLots(breweryId: $breweryId, lots: $lots) {
      id
    }
  }
`;

export const getEmptyTanksQuery = gql`
  query tanks($breweryId: Int!) {
    tanks(breweryId: $breweryId) {
      id
      name
      volume
      type
    }
  }
`;

const matchEmptyTanksToLots = (lots, emptyTanks) => {
  const emptyTanksForUse = [].concat(emptyTanks);
  return lots.map(lot => {
    const returnLot = { ...lot };
    const rightSizeTank = find(emptyTanksForUse, t => t.volume === lot.volume);
    if (rightSizeTank) {
      returnLot.tank = rightSizeTank;
      remove(emptyTanksForUse, t => t.id === rightSizeTank.id);
    }
    return returnLot;
  });
};

export class StartBrewForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      lots: matchEmptyTanksToLots(props.lots, props.emptyTanks),
      submitting: false
    };
  }

  setSubmitting = submitting => {
    this.setState({
      submitting
    });
  };

  setError = error => {
    this.setState({
      error
    });
  };

  onTankChange = (lotId, newTankId) => {
    const tank = find(this.props.emptyTanks, t => t.id == newTankId); // eslint-disable-line eqeqeq

    this.setState({
      lots: this.state.lots.map(l =>
        l.id === lotId
          ? {
              ...l,
              tank
            }
          : l
      )
    });
  };

  render() {
    const { onCancelClick, upcomingBrewCustomsReport, brewery, onSuccess, refetchQueries, emptyTanks } = this.props;
    const { submitting, lots, error } = this.state;
    const tankOptions = (
      <Fragment>
        {emptyTanks.map(t => (
          <option value={t.id}>{t.name}</option>
        ))}
      </Fragment>
    );

    return (
      <Mutation mutation={startLotsMutation}>
        {startLots => (
          <Fragment>
            <ModalBody>
              {error && <Alert color="danger">{error}</Alert>}
              <p className="mb3">Start brewing the lots in the following tanks?</p>
              <div>
                {lots.map(l => (
                  <div key={l.id} className="mb3 pb3 bb-divider">
                    <h4 className="mt0 mb2 f5">{l.reference}</h4>
                    <p>
                      {l.volume}L of {l.product.name} on {moment(l.startDate).format('dddd, MMMM Do')}
                    </p>
                    <Input
                      type="select"
                      name="tank"
                      value={l.tank ? l.tank.id : null}
                      onChange={event => this.onTankChange(l.id, event.target.value)}
                    >
                      {tankOptions}
                    </Input>
                  </div>
                ))}
              </div>
              {upcomingBrewCustomsReport && (
                <p className="mt0">
                  <em>This will also generate an upcoming brew customs report for the non-sample lots</em>
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={onCancelClick}>
                Cancel
              </Button>{' '}
              <SubmissionButton
                color="primary"
                isSubmitting={submitting}
                onClick={async () => {
                  this.setSubmitting(true);

                  try {
                    const { data } = await startLots({
                      variables: {
                        breweryId: brewery.id,
                        lots: lots.map(l => ({
                          id: l.id,
                          tankId: l.tank.id
                        }))
                      },
                      awaitRefetchQueries: true,
                      refetchQueries
                    });

                    if (data.startLots.id) {
                      const reportResponse = await request(`${config.api.url}/report/${data.startLots.id}`, {}, true);
                      await openResponse(reportResponse, 'upcoming_brew.pdf');
                    }
                    onSuccess();
                  } catch (err) {
                    console.error(err);
                    this.setError('An unknown error occured');
                    this.setSubmitting(false);
                  }
                }}
              >
                Start
              </SubmissionButton>
            </ModalFooter>
          </Fragment>
        )}
      </Mutation>
    );
  }
}

export class StartBrewModal extends Component {
  render() {
    const { lots, isOpen, onToggle, onSuccess, brewery, upcomingBrewCustomsReport, refetchQueries } = this.props;
    return (
      <Modal isOpen={isOpen} toggle={onToggle}>
        <ModalHeader toggle={onToggle}>Start brewing these lots</ModalHeader>
        <Query query={getEmptyTanksQuery} fetchPolicy="network-only" variables={{ breweryId: brewery.id }}>
          {props => {
            const { loading, error, data } = props;
            if (loading) {
              return (
                <div className="items-center flex justify-center">
                  <Loader className="pa4" />
                </div>
              );
            }

            if (error) {
              return <p className="pa4">Sorry, there was an error getting the empty beer tanks for transfer</p>;
            }

            return (
              <Formik
                validateOnChange={false}
                validateOnBlur={false}
                onSubmit={async (values, { setErrors, setSubmitting }) => {}}
                render={props => (
                  <StartBrewForm
                    {...props}
                    refetchQueries={refetchQueries}
                    brewery={brewery}
                    emptyTanks={data.tanks}
                    upcomingBrewCustomsReport={upcomingBrewCustomsReport}
                    lots={lots}
                    onCancelClick={onToggle}
                    onSuccess={onSuccess}
                  />
                )}
              />
            );
          }}
        </Query>
      </Modal>
    );
  }
}

function mapStateToProps(state) {
  return {
    brewery: state.brewery,
    upcomingBrewCustomsReport: state.brewery.country.upcomingBrewCustomsReport
  };
}
export default connect(mapStateToProps)(StartBrewModal);



// WEBPACK FOOTER //
// ./src/containers/startBrewModal/startBrewModal.jsx